import { createSelector, createStructuredSelector } from 'reselect'
import { Map, List, fromJS } from 'immutable'
import {
  get,
  getEq,
  getIn,
  getOr,
  getInOr,
  has,
  map,
  valueSeq,
  pick,
  pickToJS,
  pickToArray,
  pluck,
  sortBy,
} from 'utils/immutable'
import { timerValue } from 'utils/timers'
import { isEmptyOrNil } from 'utils/functional'
import {
  CAN,
  SIMPLE,
  CAN_INPUT,
  SERVICE,
  OUTPUT_QUERY_TYPES_WITHOUT_EVALUATION,
  BOOLEAN,
  COMPLEX,
  TIMER,
  PIN,
  DIGITAL_INPUT,
  NATIVE_INPUT,
  CONDITION_SUB_TYPES,
  ANALOG_INPUT,
  CARRY,
  SWITCH_INPUT,
  ASAM,
} from 'utils/constants'
import { OPERATORS, EVAL_VALUES, opMap } from 'components/utils'
import {
  isBooleanNativeOutput,
  isNativeOutput,
  isNonBooleanNativeOutput,
} from 'utils/types/features'

import strings from 'utils/strings'
import { createToJsSelector } from 'selectors/utils'
import {
  compose,
  range,
  isNil,
  identity,
  cond,
  always,
  T,
  both,
  includes,
  __,
  F,
  filter,
  allPass,
  curry,
  not,
} from 'ramda'

import { conditionsSelector } from 'selectors/feature/conditions'
import { getFeatureModuleName } from 'selectors/feature/featureMetadata'
import { availableLogicNodesMetaSelector } from 'selectors/feature/logicNodes'
import { signalsSelector } from 'selectors/feature/inputs'
import { harnessFloatingCavityPinsSelector } from 'selectors/feature/harnesses'
import { singleConditionStrings } from 'i18n/displayConstants/conditions'
import { DEFAULT_CURRENT } from 'constants/featureOutputs'
import { getSelectedId } from 'selectors/navigator'
import {
  getOutputConfig,
  determineDisabledInputOptions,
  RESTRICTED,
  LIMITED,
} from './utils'

const outputPropSelector =
  (prop) =>
  (state, { id }) =>
    state.getIn(['feature', 'currentFeature', 'outputs', id, prop])

const selectOutputPinsForModules = getIn(['feature', 'outputPins'])
const featureOutputsSelector = getIn(['feature', 'currentFeature', 'outputs'])
const queriesSelector = getInOr(Map(), ['feature', 'currentFeature', 'queries'])

const outputMapsSelector = getIn(['feature', 'currentFeature', 'outputMaps'])
const allOutputsSelector = getIn(['feature', 'currentFeature', 'outputs'])

const fnOutputSelector = (state, { id }) =>
  getIn(['feature', 'currentFeature', 'outputs', id], state)
const fnOutputListSelector = (state, { id }) =>
  getIn(['feature', 'currentFeature', 'outputMaps', id, 'outputs'], state)

const getPwmOutputProps = createStructuredSelector({
  pwmMode: outputPropSelector('pwmMode'),
  dutyCycle: outputPropSelector('dutyCycle'),
  dutyCycleMin: outputPropSelector('dutyCycleMin'),
  dutyCycleMax: outputPropSelector('dutyCycleMax'),
  frequency: outputPropSelector('frequency'),
  frequencyMin: outputPropSelector('frequencyMin'),
  frequencyMax: outputPropSelector('frequencyMax'),
  scalingQuery: outputPropSelector('scalingQuery'),
})

export const getOutput = createSelector(
  allOutputsSelector,
  (_, props) => props,
  (outputs, { id }) => {
    const output = outputs.get(id)

    return output.toJS()
  }
)

export const getOutputs = createSelector(allOutputsSelector, (outputs) => {
  return outputs.toJS()
})

/**
 * Outputs don't know what feeds into them. For that, we
 * have to look at the outputMap of the function that is
 * parent to a given output. Simulations needs to know the
 * relationship. This selector uses the outputs and outputMaps
 * to create a Map with output query uris as key and list of
 * output objects as the value. This way we can iterate over
 * the Map in our simulation saga
 */
const getOutputsByOutputQuery = createSelector(
  outputMapsSelector,
  allOutputsSelector,
  (outputMaps, outputs) => {
    if (!outputMaps || !outputs) {
      return Map()
    }
    const outputsByQueryUri = outputMaps
      .valueSeq() // we don't care about fnIds, so just grab the maps to iterate over
      .reduce((accumulator, fnOutputMap) => {
        const outputQueryUri = fnOutputMap.get('outputQuery')

        const outputObjects = fnOutputMap
          .get('outputs') // get the array(List?) of output IDs
          .map((outputId) => outputs.get(outputId)) // turn it into a list of output objects

        if (!outputQueryUri) {
          // if multiple functions exist with outputs that aren't yet controlled
          // by a qeuery, their `outputQueryUri` will be undefined, we need
          // to make sure they have a falsy outline when we're simulating
          return accumulator.set(
            'standalone',
            accumulator.get('standalone').merge(outputObjects)
          )
        }

        return accumulator.set(outputQueryUri, outputObjects)
      }, Map({ standalone: List() }))
    return outputsByQueryUri
  }
)

const undefinedOutputQueryOutputs = createSelector(
  outputMapsSelector,
  allOutputsSelector,
  (outputMaps, allOutputs) =>
    outputMaps.valueSeq().reduce((acc, outputMap) => {
      if (get('outputQuery', outputMap)) return acc

      const outputIds = outputMap.get('outputs')

      if (!outputIds) return acc

      const outputDescriptions = outputIds.map((id) =>
        allOutputs.getIn([id, 'description'])
      )
      return acc.concat(outputDescriptions)
    }, List())
)

const selectFnOutputs = createSelector(
  allOutputsSelector,
  fnOutputListSelector,
  (allOutputs, functionOutputIds) => {
    if (!allOutputs) return List()
    return pickToArray(functionOutputIds, allOutputs) || List()
  }
)

const getOrphanOutputDescriptions = createToJsSelector(
  undefinedOutputQueryOutputs
)

const outputPinsSelector = createSelector(
  selectOutputPinsForModules,
  getFeatureModuleName,
  (outputPinsForModules, moduleName) => {
    if (!outputPinsForModules || !moduleName) return Map()
    return outputPinsForModules.get(moduleName)
  }
)

const selectOutputPinList = createSelector(
  outputPinsSelector,
  (outputPinsForModule) => {
    if (!outputPinsForModule) return List()
    return outputPinsForModule.valueSeq()
  }
)

const selectOutputPinUris = createSelector(
  outputPinsSelector,
  (outputPinsForModule) => {
    if (!outputPinsForModule) return List()
    return outputPinsForModule.keySeq()
  }
)
const getOutputPinUris = createToJsSelector(selectOutputPinUris)
const getOutputPins = createToJsSelector(selectOutputPinList)

export const featureMappedOutputIdsSelector = createSelector(
  featureOutputsSelector,
  (outputs) => {
    if (!outputs) return List()

    return compose(
      map(get('mappedOutputId')),
      filter(get('mappedOutputId')),
      valueSeq
    )(outputs)
  }
)

export const getFeatureMappedOutputIds = createToJsSelector(
  featureMappedOutputIdsSelector
)

export const getOutputPinSwitchTransformSelector = createSelector(
  outputPinsSelector,
  (_, props) => props.mappedOutputId,
  (outputs, mappedOutputId) => {
    if (!outputs || !mappedOutputId) return Map()
    const selectedOutput = outputs.get(mappedOutputId)
    const hasSwitches = selectedOutput ? selectedOutput.has('switches') : false
    if (!hasSwitches) return Map()
    const switches = selectedOutput.get('switches')
    const transformedSw = switches.reduce((accum, currentSw) => {
      const panelNumber = currentSw.get('panelNumber')
      if (!panelNumber) return accum
      const lastDash = panelNumber.lastIndexOf('-')
      const suffix =
        lastDash >= 0 ? panelNumber.slice(lastDash + 1) : panelNumber
      if (!suffix) return accum
      if (accum.has(suffix)) return accum
      const switchesBySuffix = switches.filter((sw) => {
        const num = sw.get('panelNumber')
        if (!num) return false
        const d = num.lastIndexOf('-')
        return (d >= 0 ? num.slice(d + 1) : num) === suffix
      })
      return accum.set(suffix, switchesBySuffix)
    }, Map())
    return transformedSw
  }
)

const getOutputPinSwitches = createToJsSelector(
  getOutputPinSwitchTransformSelector
)

export const makeGetOutputPinSwitches = () => getOutputPinSwitches

const getIsOutputBoolean = createSelector(
  fnOutputListSelector,
  featureOutputsSelector,
  outputPinsSelector,
  getSelectedId,
  (_, { selectedOutputId }) => selectedOutputId,
  (fnOutputList, featureOutputs, outputs, selectedId, selectedOutputId) => {
    // get function output
    const fnOutputs = pick(fnOutputList, featureOutputs)
    const selectedFuncOutput =
      fnOutputs && fnOutputs.get(selectedOutputId || selectedId)
    // use function mapped output id to pull output data (isOutputBool flag) from signals
    if (!selectedFuncOutput || !outputs) return null
    const mappedOutputId = selectedFuncOutput.get('mappedOutputId')
    const selectedOutput = outputs.get(mappedOutputId)
    const isNativeOutputWithBooleanProp =
      selectedOutput &&
      selectedOutput.get('type') === SERVICE &&
      selectedOutput.has('isOutputBoolean')

    if (isNativeOutputWithBooleanProp) {
      return selectedOutput.get('isOutputBoolean')
    }
    return false
  }
)

const getFunctionOutput = createSelector(
  createToJsSelector(fnOutputSelector),
  createToJsSelector(outputPinsSelector),
  (featureOutput, outputs) => {
    if (isEmptyOrNil(featureOutput)) return null

    if (featureOutput.mappedOutputId) {
      // Pin-mapped output: ECU pin data is required.
      // Return null if pins have not loaded yet or the specific pin is missing,
      // to prevent consumers from acting on incomplete output metadata.
      if (isEmptyOrNil(outputs)) return null
      const selectedOutput = outputs[featureOutput.mappedOutputId]
      if (!selectedOutput) return null
      selectedOutput.outputDescription = selectedOutput.description
      const mergedOutput = { ...selectedOutput, ...featureOutput }
      const { polarity = 'AH', current = 2 } = mergedOutput
      return {
        ...mergedOutput,
        isOutputBoolean: selectedOutput.isOutputBoolean,
        polarity,
        current,
      }
    }

    // No mappedOutputId — harness-based output (e.g. ASAM, XMC NATIVE outputs).
    // No ECU pin lookup needed; return the feature output directly.
    const { polarity = 'AH', current = 2 } = featureOutput
    return {
      ...featureOutput,
      isOutputBoolean: null,
      polarity,
      current,
    }
  }
)

const getRelativeOutput = createSelector(
  createToJsSelector(outputPinsSelector),
  (_, props) => props.outputId,
  (outputs, relatedOutputUri) => {
    if (isEmptyOrNil(relatedOutputUri) || isEmptyOrNil(outputs)) return null
    const selectedOutput = outputs[relatedOutputUri]
    if (selectedOutput) {
      selectedOutput.outputDescription = selectedOutput.description
    }
    const isOutputBool = selectedOutput ? selectedOutput.isOutputBoolean : null
    const { polarity = 'AH', current = 2 } = selectedOutput || {}
    return {
      ...selectedOutput,
      isOutputBoolean: isOutputBool,
      polarity,
      current,
    }
  }
)

const getSelectedOutputBitsSelector = createSelector(
  createToJsSelector(fnOutputSelector),
  (featureOutput) => featureOutput && featureOutput.numberOfBits
)

const getNewOutputNodeIndex = createSelector(
  featureOutputsSelector,
  (outputs) => (outputs ? outputs.size + 1 : 1)
)

const outputQueryUriSelector = (state, props) =>
  state.getIn([
    'feature',
    'currentFeature',
    'outputMaps',
    props.fnId,
    'outputQuery',
  ])

export const outputQuerySelector = createSelector(
  outputQueryUriSelector,
  queriesSelector,
  conditionsSelector,
  (outputQueryUri, queries, conditions) => {
    if (!outputQueryUri || (queries.size === 0 && conditions.size === 0))
      return Map()

    const query = queries.get(outputQueryUri) || conditions.get(outputQueryUri)

    return query
  }
)
const getOutputQuery = createToJsSelector(outputQuerySelector)
const makeGetOutputQuery = () => getOutputQuery

const makeGetOutputQueryUri = () => outputQueryUriSelector

const getOutputQueryDescription = createSelector(
  availableLogicNodesMetaSelector,
  outputQueryUriSelector,
  queriesSelector,
  conditionsSelector,
  fnOutputSelector,
  (availableLogicNodes, uri, queries, conditions, output) => {
    const noNonSixteenBitOutputQueries =
      !availableLogicNodes || availableLogicNodes.isEmpty()
    if (!uri && noNonSixteenBitOutputQueries) {
      return strings.no_input_available
    }

    const selectedOutputQuery = queries.get(uri) || conditions.get(uri)

    const queryTypeEq = getEq('queryType')
    const hasDescription = has('description')
    const getDescription = get('description')

    const getTimerDescription = (outputQuery) => {
      if (!output) return strings.no_input_available
      const description = getDescription(outputQuery)
      const value = output.get('value') || 0
      const operator = output.get('operator') || 2
      return `${description} ${opMap[operator]} ${timerValue(value)}`
    }

    const getSimpleQueryDescription = (query) => {
      const subType = query.get('subType')
      return subType ? singleConditionStrings(subType) : ''
    }

    const getQueryDescription = cond([
      [queryTypeEq(SIMPLE), getSimpleQueryDescription],
      [queryTypeEq(CAN_INPUT), always(strings.can_input)],
      [queryTypeEq(TIMER), getTimerDescription],
      [hasDescription, getDescription],
      [T, always(strings.no_input_selected)],
    ])

    return getQueryDescription(selectedOutputQuery)
  }
)
const makeGetOutputQueryDescription = () => getOutputQueryDescription

const getDisabledCanOutputBits = (outputId) =>
  createSelector(featureOutputsSelector, (outputs) => {
    const numberOfBits = outputs.getIn([outputId, 'numberOfBits'])
    const reducer = (acc, current) => {
      const currentNumberOfBits = current.get('numberOfBits')
      if (
        current.get('id') === outputId ||
        current.get('numberOfBits') !== numberOfBits
      )
        return acc

      const startBit = current.get('startBit')
      if (isNil(startBit)) return acc

      return [...acc, ...range(startBit, startBit + currentNumberOfBits)]
    }

    return outputs.reduce(reducer, [])
  })

const sortByDescription = sortBy(get('description'))

export const selectOutputOptions = createSelector(
  fnOutputSelector,
  getFeatureMappedOutputIds,
  selectOutputPinList,
  harnessFloatingCavityPinsSelector,
  (_, props) => props,
  (
    output,
    featureMappedOutputIds,
    outputAssignmentOptions,
    floatingCavityPins,
    { featureModuleType }
  ) => {
    if (!output || !outputAssignmentOptions || !floatingCavityPins)
      return List()

    const isCurrentEqualToOutput = getEq('current', output.get('current'))
    const isCurrentDefault = getEq('current', DEFAULT_CURRENT)
    const isType = getEq('type', output.get('type'))
    const isPin = getEq('type', PIN)
    const isUsed = compose(includes(__, featureMappedOutputIds), get('id'))
    const isNative = getEq('type', SERVICE)
    const isDisabled = cond([
      [isPin, isUsed],
      [isNative, isUsed],
      [T, F],
    ])
    const polarityIsSame = (option) =>
      option.get('activeStateRange').includes(output.get('polarity'))
    const optionHasNoPolarity = (option) => not(has('activeStateRange', option))

    const formatOption = curry((getIsDisabled, option) =>
      fromJS({
        key: option.get('id'),
        value: option.get('id'),
        vehicleSubType: option.get('vehicleSubType'),
        disabled: getIsDisabled(option),
        title: isPin(option)
          ? `${option.get('description')}: ${option.get(
              'connector'
            )}-${option.get('location')}`
          : option.get('description'),
      })
    )

    const isApplicable = (option) => {
      if (output.get('type') === SERVICE) {
        return true
      }

      const isAsamFeatureAndCurrentIsDefault =
        featureModuleType === ASAM && isCurrentDefault(option)

      const isCurrentEqualToOuputCurrentOrDefaultCurrent =
        isCurrentEqualToOutput(option) || isAsamFeatureAndCurrentIsDefault

      const doesOptionHaveNoPolarityOrPolarityIsSame =
        optionHasNoPolarity(option) || polarityIsSame(option)

      const result =
        isCurrentEqualToOuputCurrentOrDefaultCurrent &&
        doesOptionHaveNoPolarityOrPolarityIsSame

      return result
    }

    const nonFloatingPins = outputAssignmentOptions.filter(
      (outputOption) => !floatingCavityPins.includes(outputOption.get('id'))
    )

    return compose(
      map(formatOption(isDisabled)),
      sortByDescription,
      filter(allPass([isType, isApplicable]))
    )(nonFloatingPins)
  }
)

export const getOutputOptions = createToJsSelector(selectOutputOptions)

const selectCanOutputs = createSelector(featureOutputsSelector, (outputs) =>
  outputs
    .valueSeq()
    .filter((output) => output.get('type') === CAN)
    .map((output) => pick(['numberOfBits', 'id'], output))
)

const getCanOutputs = createToJsSelector(selectCanOutputs)

// { id: fnId }
const getHasNativeOutput = createSelector(selectFnOutputs, (outputs) => {
  if (!outputs || !outputs.first()) return false
  const foundNativeOutput = outputs.find((output) =>
    isNativeOutput(output.get('type'))
  )
  return !!foundNativeOutput
})

export const getHasBooleanNativeOutput = createSelector(
  selectFnOutputs,
  (outputs) => {
    if (!outputs || !outputs.first()) return false
    const foundBooleanOutput = outputs.toJS().find(isBooleanNativeOutput)
    return !!foundBooleanOutput
  }
)

export const getHasNonBooleanNativeOutput = createSelector(
  selectFnOutputs,
  (outputs) => {
    if (!outputs || !outputs.first()) return false
    const foundNonBooleanOutput = outputs.toJS().find(isNonBooleanNativeOutput)
    return !!foundNonBooleanOutput
  }
)

const selectMappedNativeOutputs = createSelector(
  featureOutputsSelector,
  (outputs) =>
    outputs
      .valueSeq()
      .filter(
        (output) =>
          output.get('type') === SERVICE && output.get('mappedOutputId')
      )
      .map((output) => pick(['mappedOutputId', 'id'], output))
)
const getMappedNativeOutputs = createToJsSelector(selectMappedNativeOutputs)

const makeOutputAssignmentDescription = () =>
  createSelector((state, props) => {
    const outputAssignmentOptions = outputPinsSelector(state)
    const output = outputAssignmentOptions.get(props.id)

    if (!output) return strings.no_input_selected

    const connector = get('connector', output)
    const description = get('description', output)
    return connector
      ? `${description}: ${connector}-${get('location', output)}`
      : description
  }, identity)

const getIsDeletableOutput = createSelector(
  fnOutputListSelector,
  (outputs = {}) => outputs.size > 1
)

const isOutputQueryInOtherFunction = createSelector(
  outputQueryUriSelector,
  queriesSelector,
  (_, { fnId }) => fnId,
  (outputQueryUri, queries, fnId) => {
    if (!outputQueryUri) return false
    const outputQuery = get(outputQueryUri, queries)
    if (!outputQuery) return false

    const fnUrisArray = getOr(List(), 'functionUris', outputQuery)

    return !fnUrisArray.includes(fnId)
  }
)
const makeIsOutputQueryInOtherFunction = () => isOutputQueryInOtherFunction

const getOutputQuerySubType = createSelector(
  outputQuerySelector,
  (query) => query && query.get('subType')
)

const getIsSixteenBitCan = both(getEq('numberOfBits', 16), getEq('type', CAN))

export const hasSingleCondition = createSelector(
  getOutputQuerySubType,
  (outputQuerySubType) => CONDITION_SUB_TYPES.has(outputQuerySubType)
)

/**
 * This function determines the high level availability
 * of output-controlling inputs as defined by this wiki page
 * https://dtna.visualstudio.com/chec/_wiki/wikis/CHEC.wiki/695/CHECplus-Feature-Input-Driving-Output-Matrix
 * Specifically, the IO allowance matrix attributes v 5 spreadsheet
 * Some of the input types are more finely restricted. That
 * determination is made in downstream/parallel logic
 */
const getDisabledInputOptions = (output) => {
  const res = determineDisabledInputOptions(output)
  if (res === RESTRICTED) {
    /**
     * per the above referenced spreadsheet:
     * for non-ASAM features:
     * Native Outputs that are booleans
     * for ASAM features:
     * Native Outputs that are booleans
     * _cannot_ be controlled by AI Pin inputs
     */
    return {
      [NATIVE_INPUT]: false,
      [DIGITAL_INPUT]: false,
      [ANALOG_INPUT]: true,
      [CAN_INPUT]: false,
      [CARRY]: true,
    }
  }
  if (res === LIMITED) {
    /**
     * per the above referenced spreadsheet,
     * for non-ASAM features:
     * Native Outputs that are NOT booleans,
     * DO Pin with PWM current,
     * and 16 Bit CAN outputs
     * for ASAM features:
     * Native Outputs that are NOT booleans,
     * and 16 Bit CAN outputs
     * _cannot_ be controlled by DI Pin inputs
     */
    return {
      [NATIVE_INPUT]: false,
      [DIGITAL_INPUT]: true,
      [ANALOG_INPUT]: false,
      [CAN_INPUT]: false,
      [CARRY]: true,
      [SWITCH_INPUT]: true,
    }
  }

  /**
   * all othe routputs can be all the things
   */
  return {
    [NATIVE_INPUT]: false,
    [DIGITAL_INPUT]: false,
    [ANALOG_INPUT]: false,
    [CAN_INPUT]: false,
    [CARRY]: true,
  }
}

// gets single condition along with info about the output
const getSingleConditionMetaData = createSelector(
  outputQuerySelector,
  fnOutputSelector,
  signalsSelector,
  hasSingleCondition,
  (outputQuery, output, signals, hasSingleConditionFlag) => {
    if (!output) return {}
    const subTypeEq = (prop) => getEq('subType', prop, outputQuery)
    const isSixteenBitCan = getIsSixteenBitCan(output)
    const isNativeOutputCheck = getEq('type', SERVICE, output)

    const querySignal = outputQuery && outputQuery.get('signal')
    const signal = signals.get(querySignal)
    const isBooleanSignal = getEq('translationType', BOOLEAN, signal)

    const isSimple = getEq('queryType', SIMPLE, outputQuery)
    const hasSimpleDigitalInput = isSimple && subTypeEq(DIGITAL_INPUT)

    const hasSimpleNativeBooleanInput =
      isSimple && subTypeEq(NATIVE_INPUT) && isBooleanSignal
    const hasComplexOutputQuery =
      getEq('queryType', COMPLEX, outputQuery) ||
      getEq('queryType', TIMER, outputQuery)

    const disabled = getDisabledInputOptions(output)

    const singleInputData = {
      isSingleConditionOutput: hasSingleConditionFlag,
      isSixteenBitCan,
      disabled,
      isNativeOutput: isNativeOutputCheck,
      disableSixteenBit:
        hasSimpleDigitalInput ||
        hasSimpleNativeBooleanInput ||
        hasComplexOutputQuery,
      ...outputQuery?.toJS(),
    }

    return singleInputData
  }
)

const getIsSixteenBitCanOutput = createSelector(
  fnOutputListSelector,
  featureOutputsSelector,
  (fnOutputList, featureOutputs) => {
    const fnOutputs = pick(fnOutputList, featureOutputs)
    // there will only be one output for sixteen bit cans
    const output = fnOutputs && fnOutputs.first()
    if (!output) return false

    return getIsSixteenBitCan(output)
  }
)

const getOutputConfigAssessment = createSelector(
  fnOutputListSelector,
  featureOutputsSelector,
  fnOutputSelector,
  (fnOutputList, featureOutputs, actualOutput) => {
    const fnOutputs = pick(fnOutputList, featureOutputs)
    const output = fnOutputs && fnOutputs.first()
    if (!output && !actualOutput) return false
    return getOutputConfig(output || actualOutput)
  }
)

const getHasMultipleOutputs = createSelector(
  fnOutputListSelector,
  (outputs) => outputs && outputs.size > 1
)

const getOutputQueryType = createSelector(
  outputQuerySelector,
  (query) => query && query.get('queryType')
)

const getOutputSelectedQueryUri = createSelector(
  outputQuerySelector,
  (query) => query && query.get('uri')
)

const getShowOutputEvaluation = createSelector(
  getOutputQueryType,
  fnOutputSelector,
  (queryType, output) => {
    /* output after a delete can be undefined. this means there were more than 1 output, 
        which would not allow a single condition */

    if (!output) return false

    const { value, operator, type } = pickToJS(
      ['value', 'operator', 'type'],
      output
    )
    // Outputs that are CAN 16 or except only Single Conditions
    // or outputs that have inputting Timers do not have evaluations

    // to be an evaluation, the operator has to be 2 (means equal) and the value has to be set either to 1 (true) or 0 (false)
    const hasValidData =
      operator === OPERATORS.EQUAL && Object.values(EVAL_VALUES).includes(value)
    return (
      !OUTPUT_QUERY_TYPES_WITHOUT_EVALUATION.has(queryType) &&
      hasValidData &&
      !isNativeOutput(type)
    )
  }
)

const getFunctionOutputIds = createToJsSelector(
  fnOutputListSelector,
  (outputs) => {
    if (!outputs) return null
    return outputs
  }
)

const outputsToResetFloatingCircuits = createSelector(
  featureOutputsSelector,
  (_, props) => props.availableFloatingCircuits,
  (outputs, availableFloatingCircuits) => {
    const outputsToReset = outputs.filter((output) => {
      const floatingCircuit = output.get('floatingCircuit')

      if (isNil(floatingCircuit)) return false

      return !availableFloatingCircuits.includes(floatingCircuit)
    })

    return pluck('id', outputsToReset)
  }
)

export const getOutputVirtualFuseSettings = createSelector(
  getOutput,
  ({ resetAttempts, resetDelay, resetType } = {}) => {
    return {
      resetAttempts,
      resetDelay,
      resetType,
    }
  }
)

export {
  getPwmOutputProps,
  getOutputPins,
  outputPinsSelector,
  getOutputPinUris,
  getMappedNativeOutputs,
  getFunctionOutput,
  getNewOutputNodeIndex,
  getOutputQuery,
  makeGetOutputQuery,
  makeGetOutputQueryUri,
  getOutputQueryDescription,
  makeGetOutputQueryDescription,
  getDisabledCanOutputBits,
  getCanOutputs,
  makeOutputAssignmentDescription,
  getIsDeletableOutput,
  getSingleConditionMetaData,
  getOutputConfigAssessment,
  makeIsOutputQueryInOtherFunction,
  getIsSixteenBitCanOutput,
  getHasMultipleOutputs,
  getOutputSelectedQueryUri,
  getIsOutputBoolean,
  getOutputQueryType,
  getShowOutputEvaluation,
  getFunctionOutputIds,
  getOrphanOutputDescriptions,
  getHasNativeOutput,
  selectFnOutputs,
  outputsToResetFloatingCircuits,
  getOutputsByOutputQuery,
  featureOutputsSelector,
  outputMapsSelector,
  getSelectedOutputBitsSelector,
  getOutputPinSwitches,
  getRelativeOutput,
}
